/* eslint-disable indent */
import { useReducer, useCallback } from 'react';

import { AuthState, Step } from '@/slw/entities/auth/auth.type';

type ActionPayloads = {
  SET_STEP: { step: Step };
  SET_EMAIL: { email: string };
  SET_SESSION_ID: { sessionId: string };
  SET_TOKEN: { token: string };
  SET_EXPIRES: { expiresIn: number };
  SET_RESEND: { resendCooldown: number };
  SET_ERROR: { error?: string };
  SET_LOADING: { loading: boolean };
  RESET: object;
  SET_DATA: { data: Partial<AuthState> };
  SET_AUTH_TYPE: { authType: 'login' | 'register' | 'forgot' };
};

type Prettify<T> = { [K in keyof T]: T[K] } & {};

type Action = {
  [K in keyof ActionPayloads]: Prettify<{ type: K } & ActionPayloads[K]>;
}[keyof ActionPayloads];

function reducer(state: AuthState, action: Action): AuthState {
  switch (action.type) {
    case 'SET_STEP':
      return { ...state, step: action.step, error: undefined, loading: false };
    case 'SET_EMAIL':
      return { ...state, email: action.email, error: undefined };
    case 'SET_SESSION_ID':
      return { ...state, sessionId: action.sessionId };
    case 'SET_EXPIRES':
      return { ...state, expiresIn: action.expiresIn };
    case 'SET_RESEND':
      return { ...state, resendCooldown: action.resendCooldown };
    case 'SET_ERROR':
      return { ...state, error: action.error, loading: false };
    case 'SET_LOADING':
      return { ...state, loading: action.loading };
    case 'RESET':
      return { step: 'email', email: '', sessionId: '', loading: false };
    case 'SET_DATA':
      return { ...state, ...action.data };
    case 'SET_AUTH_TYPE':
      return { ...state, authType: action.authType };
    default:
      return state;
  }
}

export function useAuthFlow() {
  const [state, dispatch] = useReducer(reducer, { step: 'email', email: '', sessionId: '', loading: false });

  const setStep = useCallback((step: Step) => dispatch({ type: 'SET_STEP', step }), []);
  const setEmail = useCallback((email: string) => dispatch({ type: 'SET_EMAIL', email }), []);
  const setToken = useCallback((token: string) => dispatch({ type: 'SET_TOKEN', token }), []);
  const setExpires = useCallback((expiresIn: number) => dispatch({ type: 'SET_EXPIRES', expiresIn }), []);
  const setResend = useCallback((resendCooldown: number) => dispatch({ type: 'SET_RESEND', resendCooldown }), []);
  const setError = useCallback((error?: string) => dispatch({ type: 'SET_ERROR', error }), []);
  const setLoading = useCallback((loading: boolean) => dispatch({ type: 'SET_LOADING', loading }), []);
  const setData = useCallback((data: Partial<AuthState>) => dispatch({ type: 'SET_DATA', data }), []);
  const reset = useCallback(() => dispatch({ type: 'RESET' }), []);
  const setAuthType = useCallback(
    (authType: 'login' | 'register' | 'forgot') => dispatch({ type: 'SET_AUTH_TYPE', authType }),
    [],
  );

  return {
    state,
    setStep,
    setEmail,
    setToken,
    setExpires,
    setResend,
    setError,
    setLoading,
    setData,
    reset,
    setAuthType,
  };
}
